import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ShoppingCart, Trash2, Minus, Plus, Truck, Store, Trophy, MapPin,
  Lock, Loader2, Check, ChevronRight, ChevronLeft, Mail, Phone,
  User, Home, Calendar, Sparkles, ArrowLeft, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../../lib/useCart";
import { useSettings } from "../../lib/useSettings";
import { useCustomerAuth } from "../../lib/useCustomerAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CUSTOMER_TOKEN_KEY = "msc_customer_token";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DEEP = "#160732";

const STEPS = [
  { id: 1, label: "Panier", short: "Récap" },
  { id: 2, label: "Mode de retrait", short: "Retrait" },
  { id: 3, label: "Coordonnées", short: "Adresse" },
  { id: 4, label: "Paiement", short: "Paiement" },
];

const priceFmt = (cents, sym = "€") => `${(cents / 100).toFixed(2)} ${sym}`;

const FULFILLMENTS = [
  {
    id: "shipping",
    label: "Livraison à domicile",
    desc: "Recevez votre commande chez vous via Colissimo.",
    icon: Truck,
    color: SECONDARY,
  },
  {
    id: "pickup-match",
    label: "Retrait au prochain match",
    desc: "Récupérez vos articles le soir d'un match à la Halle Polyvalente.",
    icon: Trophy,
    color: "#f59e0b",
  },
  {
    id: "pickup-office",
    label: "Retrait au siège du club",
    desc: "Mercredi 17h-19h ou samedi 10h-12h à la Halle Polyvalente.",
    icon: Store,
    color: "#10b981",
  },
];

// ============================================================
// Stepper
// ============================================================
function Stepper({ current, onJump }) {
  return (
    <div className="flex items-center gap-1 sm:gap-3 mb-6 sm:mb-10">
      {STEPS.map((s, idx) => {
        const done = current > s.id;
        const active = current === s.id;
        return (
          <React.Fragment key={s.id}>
            <button
              type="button"
              onClick={() => done && onJump?.(s.id)}
              disabled={!done}
              className={`flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-full transition ${
                active
                  ? "bg-white text-[#4F1B7C] shadow-lg"
                  : done
                    ? "bg-pink-500/20 text-pink-200 hover:bg-pink-500/30 cursor-pointer"
                    : "bg-white/5 text-white/40"
              }`}
              data-testid={`checkout-step-${s.id}`}
            >
              <span
                className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs font-black ${
                  active ? "bg-[#EC1F7C] text-white" : done ? "bg-pink-400 text-white" : "bg-white/10 text-white/50"
                }`}
              >
                {done ? <Check size={13} /> : s.id}
              </span>
              <span className="hidden sm:inline font-black uppercase tracking-wider text-xs">{s.label}</span>
              <span className="sm:hidden font-black uppercase tracking-wider text-[10px]">{s.short}</span>
            </button>
            {idx < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 ${current > s.id ? "bg-pink-400" : "bg-white/10"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ============================================================
// Step 1 — Cart recap + cross-sells
// ============================================================
function Step1Cart({ items, subtotal_cents, currency, setQty, remove, crossSells, addProduct, donationCents }) {
  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      <div className="space-y-3" data-testid="checkout-step1-items">
        {items.map((p) => (
          <div
            key={p.line_key || p.id}
            className="flex gap-3 p-3 rounded-2xl bg-white border shadow-sm"
            data-testid={`checkout-item-${p.slug || p.id}`}
          >
            <div className="w-20 h-20 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0">
              {p.featured_image ? <img src={p.featured_image} alt={p.name} className="w-full h-full object-cover" /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-slate-900 line-clamp-2">{p.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{priceFmt(p.price_cents, currency)} · l'unité</div>
              <div className="flex items-center justify-between mt-2">
                <div className="inline-flex items-center gap-1 border rounded-full overflow-hidden bg-slate-50">
                  <button
                    type="button"
                    onClick={() => setQty(p.line_key || p.id, p.qty - 1)}
                    disabled={p.qty <= 1}
                    className="w-7 h-7 flex items-center justify-center hover:bg-slate-200 disabled:opacity-30"
                  >
                    <Minus size={12} />
                  </button>
                  <span className="min-w-[1.8rem] text-center text-sm font-bold tabular-nums">{p.qty}</span>
                  <button
                    type="button"
                    onClick={() => setQty(p.line_key || p.id, p.qty + 1)}
                    className="w-7 h-7 flex items-center justify-center hover:bg-slate-200"
                  >
                    <Plus size={12} />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-900">{priceFmt(p.price_cents * p.qty, currency)}</span>
                  <button
                    type="button"
                    onClick={() => remove(p.line_key || p.id)}
                    className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-600"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {crossSells?.length > 0 && (
          <div className="mt-6 p-4 rounded-2xl border-2 border-dashed border-pink-200 bg-pink-50/40" data-testid="checkout-cross-sells">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-pink-600" />
              <h3 className="font-black uppercase tracking-wider text-sm text-pink-700">On vous recommande aussi</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {crossSells.map((cs) => (
                <button
                  key={cs.id}
                  type="button"
                  onClick={() => addProduct(cs)}
                  className="group flex flex-col text-left p-2.5 rounded-xl bg-white border hover:border-pink-400 hover:shadow-md transition"
                  data-testid={`crosssell-${cs.id}`}
                >
                  <div className="aspect-square rounded-lg bg-slate-100 overflow-hidden mb-2">
                    {cs.image ? <img src={cs.image} alt={cs.name} className="w-full h-full object-cover group-hover:scale-105 transition" /> : null}
                  </div>
                  <div className="font-bold text-xs text-slate-900 line-clamp-2">{cs.name}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="font-black text-sm text-pink-600">{priceFmt(cs.price_cents, currency)}</span>
                    <span className="text-[10px] font-black uppercase tracking-wider text-pink-600 group-hover:text-white group-hover:bg-pink-500 px-2 py-1 rounded-full bg-pink-100 transition">+ Ajouter</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <CartSummary items={items} subtotal_cents={subtotal_cents} currency={currency} donationCents={donationCents} />
    </div>
  );
}

function CartSummary({ items, subtotal_cents, currency, shippingCents = 0, donationCents = 0, label = "Récapitulatif" }) {
  const total = subtotal_cents + shippingCents + donationCents;
  return (
    <aside className="lg:sticky lg:top-24 self-start p-5 rounded-2xl bg-white border shadow-lg" data-testid="checkout-summary">
      <h3 className="font-black uppercase tracking-wider text-xs text-slate-700 mb-3">{label}</h3>
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-slate-600">Sous-total ({items.length} {items.length > 1 ? "articles" : "article"})</span>
          <span className="font-bold">{priceFmt(subtotal_cents, currency)}</span>
        </div>
        {shippingCents > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-slate-600">Livraison</span>
            <span className="font-bold tabular-nums">{priceFmt(shippingCents, currency)}</span>
          </div>
        )}
        {donationCents > 0 && (
          <div className="flex items-center justify-between text-pink-700">
            <span>Don au club</span>
            <span className="font-bold tabular-nums">+ {priceFmt(donationCents, currency)}</span>
          </div>
        )}
      </div>
      <div className="border-t mt-4 pt-3 flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider font-black text-slate-700">Total TTC</span>
        <span className="text-2xl font-black text-[#4F1B7C] tabular-nums" data-testid="checkout-total">{priceFmt(total, currency)}</span>
      </div>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-2 text-center">Paiement sécurisé Stripe · TVA incluse</p>
    </aside>
  );
}

// ============================================================
// Step 2 — Fulfillment mode
// ============================================================
function Step2Fulfillment({ mode, setMode, options, pickupChoice, setPickupChoice, currency }) {
  return (
    <div className="space-y-5" data-testid="checkout-step2">
      <h2 className="text-2xl font-black text-slate-900" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em" }}>
        Comment souhaitez-vous récupérer votre commande ?
      </h2>

      <div className="grid md:grid-cols-3 gap-3">
        {FULFILLMENTS.map((f) => {
          const Icon = f.icon;
          const sel = mode === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => { setMode(f.id); setPickupChoice(null); }}
              className={`flex flex-col text-left p-4 rounded-2xl border-2 transition relative ${
                sel ? "border-[#EC1F7C] shadow-xl bg-pink-50/50" : "border-slate-200 hover:border-pink-300 bg-white"
              }`}
              data-testid={`fulfillment-${f.id}`}
            >
              <div className="flex items-center gap-2.5 mb-2">
                <span className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: f.color }}>
                  <Icon size={18} />
                </span>
                <h3 className="font-black uppercase text-sm tracking-wider text-slate-900">{f.label}</h3>
              </div>
              <p className="text-xs text-slate-600 leading-snug">{f.desc}</p>
              {sel && (
                <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#EC1F7C] text-white flex items-center justify-center">
                  <Check size={14} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {mode === "pickup-match" && options?.match_pickups?.length > 0 && (
          <motion.div
            key="match"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-2"
            data-testid="match-pickup-list"
          >
            <h3 className="font-black uppercase tracking-wider text-xs text-slate-700">Choisissez le match :</h3>
            {options.match_pickups.map((m) => {
              const sel = pickupChoice?.id === m.id;
              const date = new Date(m.kickoff_iso);
              const dateLabel = date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setPickupChoice({ id: m.id, slot_at: m.kickoff_iso, location_name: m.label })}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition text-left ${
                    sel ? "border-[#EC1F7C] bg-pink-50" : "border-slate-200 hover:border-pink-300 bg-white"
                  }`}
                  data-testid={`match-pickup-${m.id}`}
                >
                  {m.opponent_logo && (
                    <img src={m.opponent_logo} alt={m.opponent_team} className="w-12 h-12 object-contain" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm text-slate-900 uppercase">{options.club_name} vs {m.opponent_team}</div>
                    <div className="text-xs text-slate-600 mt-0.5 inline-flex items-center gap-1.5"><Calendar size={11} />{dateLabel} · {m.time}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1"><MapPin size={10} />{m.venue}</div>
                  </div>
                  {sel && <span className="w-6 h-6 rounded-full bg-[#EC1F7C] text-white flex items-center justify-center"><Check size={14} /></span>}
                </button>
              );
            })}
          </motion.div>
        )}

        {mode === "pickup-office" && options?.office_pickup?.slots?.length > 0 && (
          <motion.div
            key="office"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-2"
            data-testid="office-pickup-list"
          >
            <h3 className="font-black uppercase tracking-wider text-xs text-slate-700">Choisissez un créneau :</h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {options.office_pickup.slots.map((iso) => {
                const sel = pickupChoice?.slot_at === iso;
                const d = new Date(iso);
                const label = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
                const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setPickupChoice({ id: "iphb-office", slot_at: iso, location_name: options.office_pickup.venue })}
                    className={`p-3 rounded-xl border-2 transition text-left ${
                      sel ? "border-[#EC1F7C] bg-pink-50" : "border-slate-200 hover:border-pink-300 bg-white"
                    }`}
                    data-testid={`office-slot-${iso}`}
                  >
                    <div className="font-bold text-sm text-slate-900 capitalize">{label}</div>
                    <div className="text-xs text-slate-600 inline-flex items-center gap-1 mt-0.5">
                      <Calendar size={11} /> {time}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">{options.office_pickup.info}</p>
          </motion.div>
        )}

        {mode === "shipping" && (
          <motion.div
            key="shipping"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="p-4 rounded-2xl bg-slate-50 border border-slate-200"
          >
            <p className="text-sm text-slate-700">
              Livraison <strong>Colissimo</strong> à domicile · 5,90 € · 2-3 jours ouvrés (gratuit dès 80 €).
              Vous renseignerez votre adresse à l'étape suivante.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================
// Step 3 — Address / contact info
// ============================================================
function Step3Address({ mode, address, setAddress, contact, setContact }) {
  if (mode === "shipping") {
    return (
      <div className="space-y-3" data-testid="checkout-step3-shipping">
        <h2 className="text-2xl font-black text-slate-900 mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em" }}>
          Adresse de livraison
        </h2>
        <div className="grid md:grid-cols-2 gap-3">
          <Input label="Prénom" value={address.first_name} onChange={(v) => setAddress({ ...address, first_name: v })} testid="addr-first-name" required />
          <Input label="Nom" value={address.last_name} onChange={(v) => setAddress({ ...address, last_name: v })} testid="addr-last-name" required />
          <Input label="Email" type="email" value={address.email} onChange={(v) => setAddress({ ...address, email: v })} testid="addr-email" required />
          <Input label="Téléphone" type="tel" value={address.phone} onChange={(v) => setAddress({ ...address, phone: v })} testid="addr-phone" required />
          <div className="md:col-span-2"><Input label="Adresse" value={address.street} onChange={(v) => setAddress({ ...address, street: v })} testid="addr-street" required /></div>
          <Input label="Code postal" value={address.postal_code} onChange={(v) => setAddress({ ...address, postal_code: v })} testid="addr-postal" required />
          <Input label="Ville" value={address.city} onChange={(v) => setAddress({ ...address, city: v })} testid="addr-city" required />
          <Input label="Pays" value={address.country} onChange={(v) => setAddress({ ...address, country: v })} testid="addr-country" />
          <Input label="Complément (optionnel)" value={address.notes} onChange={(v) => setAddress({ ...address, notes: v })} testid="addr-notes" />
        </div>
      </div>
    );
  }

  // Pickup contact info
  return (
    <div className="space-y-3" data-testid="checkout-step3-pickup">
      <h2 className="text-2xl font-black text-slate-900 mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em" }}>
        Vos coordonnées pour le retrait
      </h2>
      <p className="text-sm text-slate-600">Nous vous enverrons une confirmation et vous contacterons en cas de besoin.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Input label="Prénom" value={contact.first_name} onChange={(v) => setContact({ ...contact, first_name: v })} testid="contact-first-name" required />
        <Input label="Nom" value={contact.last_name} onChange={(v) => setContact({ ...contact, last_name: v })} testid="contact-last-name" required />
        <Input label="Email" type="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} testid="contact-email" required />
        <Input label="Téléphone" type="tel" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} testid="contact-phone" required />
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">{label}{required && <span className="text-pink-600 ml-1">*</span>}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-11 px-3 rounded-xl border border-slate-300 bg-white text-sm focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none transition"
        data-testid={testid}
        required={required}
      />
    </label>
  );
}

// ============================================================
// Step 4 — Payment
// ============================================================
function Step4Payment({ totalCents, currency, cgvAccepted, setCgvAccepted, summary }) {
  return (
    <div className="space-y-5" data-testid="checkout-step4">
      <h2 className="text-2xl font-black text-slate-900" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em" }}>
        Confirmation et paiement
      </h2>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
          <h3 className="font-black uppercase tracking-wider text-xs text-slate-700 mb-2">Récap retrait</h3>
          {summary?.mode_label && <p className="text-sm font-bold text-slate-900">{summary.mode_label}</p>}
          {summary?.subtitle && <p className="text-xs text-slate-600 mt-1">{summary.subtitle}</p>}
          {summary?.address && (
            <p className="text-xs text-slate-700 mt-2 leading-relaxed whitespace-pre-line">{summary.address}</p>
          )}
        </div>

        <div className="p-4 rounded-2xl border-2 border-pink-200 bg-pink-50/50">
          <h3 className="font-black uppercase tracking-wider text-xs text-pink-700 mb-2 flex items-center gap-1.5"><ShieldCheck size={14} />Mode de paiement</h3>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-white border flex items-center justify-center">
              <Lock size={16} className="text-pink-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900">Carte bancaire (Stripe)</p>
              <p className="text-xs text-slate-600">Visa · Mastercard · Amex · Apple Pay · Google Pay</p>
            </div>
          </div>
        </div>
      </div>

      <label className="flex items-start gap-2.5 p-3.5 rounded-xl border bg-white text-sm cursor-pointer hover:border-pink-300" data-testid="checkout-cgv-wrapper">
        <input
          type="checkbox"
          checked={cgvAccepted}
          onChange={(e) => setCgvAccepted(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-pink-600"
          data-testid="checkout-cgv-checkbox"
        />
        <span className="text-slate-700 leading-snug">
          J'ai lu et j'accepte les <Link to="/sport/legal/cgv" target="_blank" rel="noopener" className="underline font-semibold text-pink-600">conditions générales de vente</Link> et la <Link to="/sport/legal/confidentialite" target="_blank" rel="noopener" className="underline font-semibold text-pink-600">politique de confidentialité</Link>.
        </span>
      </label>
    </div>
  );
}

// ============================================================
// MAIN PAGE
// ============================================================
export default function SportCheckout() {
  const navigate = useNavigate();
  const { items, subtotal_cents, count, setQty, remove, add, drawerOpen, setDrawerOpen } = useCart();
  const { settings: s } = useSettings();
  const { customer } = useCustomerAuth();

  const [step, setStep] = useState(1);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [mode, setMode] = useState("shipping");
  const [pickupChoice, setPickupChoice] = useState(null); // {id, slot_at, location_name}
  const [address, setAddress] = useState({
    first_name: customer?.first_name || "",
    last_name: customer?.last_name || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
    street: "", postal_code: "", city: "", country: "France", notes: "",
  });
  const [contact, setContact] = useState({
    first_name: customer?.first_name || "",
    last_name: customer?.last_name || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
  });
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [donationCents, setDonationCents] = useState(0);

  const currency = s?.shop_currency_symbol || "€";

  // Close cart drawer if open
  useEffect(() => {
    if (drawerOpen) setDrawerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch checkout options
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/sport/checkout/options`);
        if (alive) setOptions(data);
      } catch (e) {
        toast.error("Impossible de charger les options de retrait");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Redirect if cart empty (but allow brief grace period for hydration)
  useEffect(() => {
    if (count === 0 && !submitting) {
      const t = setTimeout(() => {
        if (count === 0) navigate("/sport/boutique");
      }, 400);
      return () => clearTimeout(t);
    }
  }, [count, submitting, navigate]);

  // Shipping cost (estimated)
  const shippingCents = useMemo(() => {
    if (mode !== "shipping") return 0;
    if (subtotal_cents >= 8000) return 0;
    return 590;
  }, [mode, subtotal_cents]);

  // Validation per step
  const canNext = useMemo(() => {
    if (step === 1) return items.length > 0;
    if (step === 2) {
      if (mode === "shipping") return true;
      return !!pickupChoice;
    }
    if (step === 3) {
      if (mode === "shipping") {
        return ["first_name", "last_name", "email", "phone", "street", "postal_code", "city"].every((k) => (address[k] || "").trim());
      }
      return ["first_name", "last_name", "email", "phone"].every((k) => (contact[k] || "").trim());
    }
    if (step === 4) return cgvAccepted;
    return false;
  }, [step, items, mode, pickupChoice, address, contact, cgvAccepted]);

  // Add cross-sell to cart
  const addCrossSell = (cs) => {
    add({
      id: cs.id,
      slug: cs.id,
      name: cs.name,
      price_cents: cs.price_cents,
      featured_image: cs.image,
      stock_tracking: false,
      stock_qty: 99,
    }, 1);
    setDrawerOpen(false);
    toast.success(`${cs.name} ajouté au panier`);
  };

  const summary = useMemo(() => {
    const fObj = FULFILLMENTS.find((f) => f.id === mode);
    if (mode === "shipping") {
      return {
        mode_label: fObj?.label,
        subtitle: "Colissimo 2-3 jours ouvrés",
        address: `${address.first_name} ${address.last_name}\n${address.street}\n${address.postal_code} ${address.city}, ${address.country}\n${address.phone}`,
      };
    }
    if (!pickupChoice) return null;
    const dt = new Date(pickupChoice.slot_at);
    return {
      mode_label: fObj?.label,
      subtitle: pickupChoice.location_name,
      address: `${contact.first_name} ${contact.last_name}\n${dt.toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}\n${contact.phone}`,
    };
  }, [mode, pickupChoice, address, contact]);

  const onSubmit = async () => {
    if (!cgvAccepted) {
      toast.error("Vous devez accepter les CGV");
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem(CUSTOMER_TOKEN_KEY) || "";
      const payload = {
        items: items.map((i) => ({ product_id: i.id, qty: i.qty, variant_id: i.variant_id || undefined })),
        origin_url: window.location.origin,
        customer_email: (mode === "shipping" ? address.email : contact.email) || customer?.email || undefined,
        cgv_accepted: true,
        fulfillment_type: mode === "shipping" ? "shipping" : "pickup",
      };
      if (mode !== "shipping" && pickupChoice) {
        payload.pickup_location_id = pickupChoice.id;
        payload.pickup_slot_at = pickupChoice.slot_at;
      }
      if (donationCents > 0) {
        payload.tip_cents = donationCents;
        payload.tip_kind = "donation";
      }
      const { data } = await axios.post(
        `${API}/checkout/create-session`,
        payload,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!data?.url) throw new Error("URL Stripe manquante");
      window.location.href = data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossible de démarrer le paiement");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-svh flex items-center justify-center" style={{ background: BG_DEEP }}>
        <Loader2 className="w-10 h-10 text-pink-400 animate-spin" />
      </div>
    );
  }

  const totalCents = subtotal_cents + shippingCents + donationCents;

  return (
    <div className="min-h-svh pb-32" style={{ background: `linear-gradient(180deg, ${BG_DEEP} 0%, ${PRIMARY} 100%)` }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6 sm:mb-8">
          <Link to="/sport/boutique" className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-bold transition" data-testid="checkout-back-shop">
            <ArrowLeft size={16} />Retour à la boutique
          </Link>
          <h1
            className="text-3xl sm:text-5xl text-white text-center font-black uppercase tracking-wider"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
            data-testid="checkout-page-title"
          >
            Commande
          </h1>
          <div className="w-24 hidden sm:block" />
        </div>

        <Stepper current={step} onJump={setStep} />

        {/* Body */}
        <div className="bg-white/97 rounded-3xl shadow-2xl p-5 sm:p-8 backdrop-blur">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              {step === 1 && (
                <Step1Cart
                  items={items}
                  subtotal_cents={subtotal_cents}
                  currency={currency}
                  setQty={setQty}
                  remove={remove}
                  crossSells={options?.cross_sells || []}
                  addProduct={addCrossSell}
                  donationCents={donationCents}
                />
              )}
              {step === 2 && (
                <Step2Fulfillment
                  mode={mode}
                  setMode={setMode}
                  options={options}
                  pickupChoice={pickupChoice}
                  setPickupChoice={setPickupChoice}
                  currency={currency}
                />
              )}
              {step === 3 && (
                <Step3Address
                  mode={mode}
                  address={address}
                  setAddress={setAddress}
                  contact={contact}
                  setContact={setContact}
                />
              )}
              {step === 4 && (
                <>
                  <Step4Payment
                    totalCents={totalCents}
                    currency={currency}
                    cgvAccepted={cgvAccepted}
                    setCgvAccepted={setCgvAccepted}
                    summary={summary}
                  />
                  <div className="mt-6">
                    <CartSummary
                      items={items}
                      subtotal_cents={subtotal_cents}
                      currency={currency}
                      shippingCents={shippingCents}
                      donationCents={donationCents}
                      label="Total à régler"
                    />
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Nav buttons */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => (step > 1 ? setStep(step - 1) : navigate("/sport/boutique"))}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-sm transition disabled:opacity-50"
            data-testid="checkout-back-btn"
          >
            <ChevronLeft size={16} />{step > 1 ? "Retour" : "Boutique"}
          </button>

          {step < 4 ? (
            <button
              type="button"
              onClick={() => canNext && setStep(step + 1)}
              disabled={!canNext}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-black uppercase tracking-wider text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "linear-gradient(135deg, #EC1F7C 0%, #ff3a8c 100%)",
                color: "#fff",
                boxShadow: "0 8px 28px -10px rgba(236,31,124,0.7)",
              }}
              data-testid="checkout-next-btn"
            >
              Continuer<ChevronRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              onClick={onSubmit}
              disabled={!canNext || submitting}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-black uppercase tracking-wider text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "linear-gradient(135deg, #EC1F7C 0%, #ff3a8c 100%)",
                color: "#fff",
                boxShadow: "0 10px 30px -10px rgba(236,31,124,0.8)",
              }}
              data-testid="checkout-pay-btn"
            >
              {submitting ? (<><Loader2 size={16} className="animate-spin" />Redirection Stripe…</>) : (<><Lock size={16} />Payer {priceFmt(totalCents, currency)}</>)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
